import { ActionIcon, Group, Tooltip } from "@mantine/core"
import {
  CheckIcon, ChecksIcon, EyeIcon, FileTextIcon,
  PencilSimpleIcon, XCircleIcon, XIcon,
} from "@phosphor-icons/react"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { SalesAction, SalesActionState, SalesOrderLike } from "../../../components/order/order-actions"
import { ReasonTooltip } from "../../../components/ui"
import { IOrderPlus } from "../../../models/Order"
import classes from "../list.module.css"
import { RowActionKey } from "./types"

type Props = {
  record: IOrderPlus
  stateOf: (action: SalesAction, order: SalesOrderLike | null | undefined) => SalesActionState
  /** Split tab: only the detail action. */
  detailOnly?: boolean
  onAction: (key: RowActionKey, record: IOrderPlus) => void
}

type Item = { action: SalesAction; key: RowActionKey; label: string; icon: ReactNode }

/**
 * Row actions live in the FIRST column as grey icon buttons. What exists per status comes from the one
 * status x action matrix (components/order/order-actions); an action without permission stays visible,
 * aria-disabled, with «Эрх хэрэгтэй: КОД».
 */
export function RowActions({ record, stateOf, onAction, detailOnly }: Props) {
  const { t } = useTranslation(["supplierSales", "common"])
  // Written on delivery completion and carried on the list row, so a delivered order offers its
  // зарлагын баримт without a detour through Хүргэлт удирдлага.
  const issueNotePdf = record?.deliveryNote?.pdf

  const items: Item[] = [
    { action: "detail", key: "detail", label: t("common:actions.detail"), icon: <EyeIcon size={18} /> },
    { action: "edit", key: "edit", label: t("common:actions.edit"), icon: <PencilSimpleIcon size={18} /> },
    { action: "review", key: "reviewed", label: t("supplierSales:sales.review"), icon: <CheckIcon size={18} /> },
    { action: "approve", key: "confirm", label: t("supplierSales:sales.approve"), icon: <ChecksIcon size={18} /> },
    { action: "decline", key: "reject", label: t("supplierSales:sales.decline"), icon: <XCircleIcon size={18} /> },
    { action: "cancel", key: "cancel", label: t("supplierSales:sales.cancel"), icon: <XIcon size={18} /> },
  ]

  return (
    <Group gap="xs" wrap="nowrap">
      {items.map((item) => {
        const state = stateOf(item.action, record)
        if (!state.applies || (detailOnly && item.action !== "detail")) return null
        const button = (
          <ActionIcon
            variant="light"
            color="gray"
            size={32}
            radius="md"
            className={classes.rowAction}
            aria-label={item.label}
            onClick={(e) => {
              e.stopPropagation()
              onAction(item.key, record)
            }}
          >
            {item.icon}
          </ActionIcon>
        )
        return state.allowed ? (
          <Tooltip key={item.key} label={item.label} position="bottom" withArrow openDelay={150}>
            {button}
          </Tooltip>
        ) : (
          <ReasonTooltip key={item.key} reason={`${item.label}. ${state.reason}`}>
            {button}
          </ReasonTooltip>
        )
      })}
      {!detailOnly && issueNotePdf && (
        <Tooltip label={t("supplierSales:sales.downloadIssueNote")} position="bottom" withArrow openDelay={150}>
          <ActionIcon
            variant="light"
            color="gray"
            size={32}
            radius="md"
            className={classes.rowAction}
            aria-label={t("supplierSales:sales.downloadIssueNote")}
            onClick={() => window.open(issueNotePdf, "_blank", "noopener,noreferrer")}
          >
            <FileTextIcon size={18} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  )
}
